<template lang="pug">
  section.loading-dots(v-if="singlePatientDetailsLoading || !patient")
    .dot1
    .dot2
    .dot3
  section.patient-detail-section(v-else-if="patient")
    ul.patient-header
      li.name
        h3 {{ patient.firstName }}
      li.email
        p {{ patient.emailAddress }}
      li.access-code
        p {{ patient.accessCode }}
      li.engagement
        assessment-icon(:assessment="assessment" v-for="(assessment, index) in latestScores(patient)" :key="index")
        dl.interaction
          dt Recent Interaction
          dd {{ lastInteractionDt(patient) | shortDate }} {{ lastInteraction(patient) }}
    .patient-detail-content
      nav.tabs
        ul
          li
            a(href="#" v-bind:class="{active:tabs.showProfile}" v-on:click.prevent="setActive('showProfile')") Profile
          li
            a(href="#" v-bind:class="{active:tabs.showAssessment}" v-on:click.prevent="setActive('showAssessment')") Assessment Scores
          li
            a(href="#" v-bind:class="{active:tabs.showActivity}" v-on:click.prevent="setActive('showActivity')") Activity History
          li
            a(href="#" v-bind:class="{active:tabs.showTracker}" v-on:click.prevent="setActive('showTracker')") Tracker History
      section.tab-content
        patient-profile(v-if="tabs.showProfile" :patient="patient")
        patient-assessment(v-if="tabs.showAssessment" :assessments="patient.assessmentScores")
        patient-activity(v-if="tabs.showActivity" :activities="singlePatientActivities")
        patient-tracker(v-if="tabs.showTracker" :trackerHistory="patient.trackerHistory")
</template>

<script>
import PatientProfile from '@/components/patient/detail/PatientProfile.vue';
import PatientAssessment from '@/components/patient/detail/PatientAssessment.vue';
import PatientActivity from '@/components/patient/detail/PatientActivity.vue';
import PatientProgram from '@/components/patient/detail/PatientProgram.vue';
import PatientTracker from '@/components/patient/detail/PatientTracker.vue';

import AssessmentIcon from './patient/AssessmentIcon';
import lastInteractionMixin from '@/mixins/lastInteraction.js';
import latestScoresMixin from '@/mixins/latestScores.js';

import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'SinglePatient',
  components: {
    PatientProfile,
    PatientAssessment,
    PatientActivity,
    PatientProgram,
    PatientTracker,
    AssessmentIcon
  },
  data() {
    return {
      userHasCoaching: false,
      coachingChatUpdate: new Date().getTime(),
      canEnableReview: false,
      tabs: {
        showProfile: true,
        showAssessment: false,
        showActivity: false,
        showProgram: false,
        showTracker: false
      }
    };
  },
  computed: {
    patient() {
      return this.getSinglePatientDetailsById(this.id);
    },
    ...mapGetters([
      'getSinglePatientDetailsById',
      'singlePatientDetailsLoading',
      'singlePatientNotFound',
      'singlePatientActivities'
    ])
  },
  // Prop id passed from route
  props: [ 'id' ],
  methods: {
    init() {
      this.setQueryParamData();
      this.getSinglePatientDetails(this.id)
        .then(() => {
          // Enable coaching chat if user is enrolled in coaching
          if (this.patient && this.patient.coachingUser) {
            this.userHasCoaching = true;
          }

          if (this.singlePatientNotFound) {
            this.$router.push({name: 'SinglePatientNotFound'});
          } else {
            this.getSinglePatientActivityHistory(this.id).finally(this.doLogout);
          }
        });
    },
    setActive(tab) {
      const tabs = this.tabs;
      Object.keys(this.tabs).forEach(function(key) {
        tabs[key] = (key === tab);
      });
    },
    setQueryParamData() {
      // Checking string true OR boolean due to inconsistencies in vue router
      // Reload causes string true & navigation route sets boolean
      this.isCoachView = this.$route.query.coaching === 'true' || this.$route.query.coaching === true;
      this.isSupervisorView = this.$route.query.supervisor === 'true' || this.$route.query.supervisor === true;
    },
    doLogout() {
      setTimeout(() => {
        this.logoutUser();
      }, 1000);
    },
    ...mapActions([
      'getSinglePatientDetails',
      'getSinglePatientActivityHistory',
      'logoutUser'
    ])
  },
  mixins: [ lastInteractionMixin, latestScoresMixin ],
  created() {
    this.init();
  }
};
</script>

<style lang="scss" scoped>
  .patient-header {
    width: 100%;
    margin: 0 auto;
    padding: $small-spacing;
    background: #fff;
    border-bottom: 1px solid $md-grey-200;
    li {
      display: inline-block;
      padding-bottom: $xs-spacing;
      padding-right: $base-spacing;
      h3, p {
        @include zero;
      }
    }
    li.engagement {
      display: block;
      padding: 0;
      padding-top: $small-spacing;
      border-top: 1px solid $black;
    }
    .name {
      h3 {
        line-height: 1;
        font-size: $ms-up2;
      }
    }
  }
  .interaction {
    display: inline-block;
    @include zero;
    text-align: center;
    padding: 0 $small-spacing;

    dt,
    dd {
      @include zero;
    }
    dt {
      line-height: 1;
      text-transform: uppercase;
      font-size: $ms-down1;
    }
  }
  .patient-detail-content {
    margin: 0 auto;
    background: #fff;
    display: flex;
    justify-content: space-apart;
    align-content: stretch;
  }
  .tabs {
    // width: 20%;
    flex: 0 0 20%;
    min-width: 12em;
    border-right: 1px solid $md-grey-200;
    ul {
      padding-bottom: $small-spacing;
      li {
        display: block;
        text-transform: uppercase;
        line-height: 1.2;
      }
      a {
        display: block;
        position: relative;
        padding: $small-spacing;
        border-bottom: 1px solid $md-grey-200;
        color: $grey;
        &:hover, &:focus {
          color: $black;
        }
      }
      a.active {
        color: $action-color;
        font-weight: $base-font-weight-strong;
        &:after {
          content: '';
          position: absolute;
          display: block;
          top: 50%;
          margin-top: -6px;
          left: -6px;
          @include size(12px);
          background: $action-color;
          border-radius: 12px;
        }
      }
    }
  }
  .tab-content {
    padding: $small-spacing;
    flex: 1 1 auto;
    min-width: 50%;
    overflow-y: auto;
    max-height: 90vh;
  }
</style>
